<template>
  <Teleport to="body">
    <Transition name="dk-drawer">
      <div v-if="cart.open" class="dk-drawer-root" role="dialog" aria-modal="true" aria-label="Cart">
        <div class="dk-drawer__backdrop" @click="cart.closeCart()" />
        <aside class="dk-drawer__panel" :class="`dk-drawer__panel--${width}`" :style="panelStyle">
          <header class="dk-drawer__head">
            <!-- "Cart • 3 items" rather than "Cart (3)": the count is the second
                 thing a buyer checks after the total, so it is set in the same
                 weight as the heading instead of parenthesised beside it. -->
            <strong>{{ heading }}<span v-if="cart.count" class="dk-drawer__count"> • {{ countLabel }}</span></strong>
            <button class="dk-drawer__x" aria-label="Close" @click="cart.closeCart()">
              <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true">
                <path d="M5 5l14 14M19 5L5 19" />
              </svg>
            </button>
          </header>

          <!-- Drawer body = the `main-cart` section's BLOCKS, in the seller's
               order. `line-items` is itself a block and takes the flex space +
               its own scroll, which reproduces the head/body/footer split
               without the theme modelling three regions. Falls back to the
               default order for themes with no main-cart section.
               The wrapper is the panel-level scroll (CS10): when the fixed
               blocks alone are taller than the panel, the blocks scroll as one,
               so the checkout button is always reachable. The header stays. -->
          <div class="dk-drawer__scroll">
            <CartBlocks />
          </div>
        </aside>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ownWidth } from "@dk/theme"
import { computed, onBeforeUnmount, watch } from "vue"
import { useRoute } from "vue-router"
import { useCartBlockHost } from "../lib/cartblockhost"
import { useBootstrapStore } from "../stores/bootstrap"
import { useCartStore } from "../stores/cart"
import CartBlocks from "./cart/CartBlocks.vue"

const cart = useCartStore()
const bootstrap = useBootstrapStore()
const route = useRoute()

// Hand the blocks their state. The composable owns the timers, the upsell fetch
// and the theme-setting fallbacks — and the cart PAGE calls the same thing, so
// `CartBlocks` renders identically in both hosts instead of throwing on /cart.
// Only the drawer gates on `open`; everything below is drawer chrome.
useCartBlockHost(computed(() => cart.open))

// The cart page's `main-cart` section owns the drawer's blocks.
const mainCartSection = computed(() =>
  (bootstrap.themeDoc?.pages?.cart?.sections ?? []).find((s) => s.type === "main-cart"),
)
// Block resolution moved to useCartBlockHost, which the cart PAGE now shares.
const sectionSettings = computed<Record<string, unknown>>(() => mainCartSection.value?.settings ?? {})
const heading = computed(() => String(sectionSettings.value.heading ?? "Your cart"))
// Counts every unit the buyer is paying for, accepted add-ons included — the same
// number the server put in `item_count`, so the header can never disagree with the
// subtotal below it.
const countLabel = computed(() => `${cart.count} ${cart.count === 1 ? "item" : "items"}`)
// `drawer_width` since the rename — `ownWidth` reads a pre-rename `width` when
// it holds this section's vocabulary (normal | wide | full).
const width = computed(() => String(ownWidth("main-cart", sectionSettings.value) ?? "normal"))
const panelStyle = computed(() => {
  const bg = String(sectionSettings.value.background ?? "")
  return bg ? { background: bg } : undefined
})

// Close on navigation.
watch(() => route.fullPath, () => cart.closeCart())

// Close on Escape + lock page scroll while open. Both `documentElement` and
// `body` are set: `styles.css` gives `html` its own `overflow-x`, which disables
// the browser's standard propagation of body's overflow to the viewport, so
// `<html>` — not `<body>` — is the element actually scrolling the page. Locking
// body alone left the page scrollbar live and scrollable behind the drawer.
function onKey(e: KeyboardEvent) {
  if (e.key === "Escape") cart.closeCart()
}
watch(
  () => cart.open,
  (open) => {
    document.documentElement.style.overflow = open ? "hidden" : ""
    document.body.style.overflow = open ? "hidden" : ""
    if (open) window.addEventListener("keydown", onKey)
    else window.removeEventListener("keydown", onKey)
  },
)

onBeforeUnmount(() => {
  document.documentElement.style.overflow = ""
  document.body.style.overflow = ""
  window.removeEventListener("keydown", onKey)
})
</script>

<style scoped>
.dk-drawer-root {
  position: fixed;
  inset: 0;
  z-index: 60;
}
.dk-drawer__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
}
.dk-drawer__panel {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: min(420px, 92vw);
  display: flex;
  flex-direction: column;
  background: var(--dk-color-bg, #fff);
  color: var(--dk-color-fg, #111);
  box-shadow: -12px 0 40px rgba(0, 0, 0, 0.18);
}
/* The blocks' column, and the panel-level scroll. It is the flex column the blocks
   used to sit in directly, so with room to spare nothing changes: `line-items`
   still takes the free height and scrolls its own rows. Only when the fixed
   blocks alone overflow, `line-items` is down to its 4.5rem floor and this
   scrolls instead, where before the tail, checkout button included, was cut
   off with nothing to scroll. */
.dk-drawer__scroll {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overscroll-behavior: contain;
}
/* The heading is set large deliberately: it is the drawer's title bar, and at the
   old 1.05rem it read as a label on a panel rather than the panel's name. No bottom
   border — the countdown band below supplies the separation, and on a drawer without
   one the item rows' own rules do. */
.dk-drawer__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.1rem 1.25rem;
  font-size: 1.65rem;
  line-height: 1.15;
}
.dk-drawer__count {
  font-weight: inherit;
}
.dk-drawer__x {
  flex: none;
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  /* 2px more than the 40px button's -.5rem, so the cross stays where it was. */
  margin-right: -10px;
  background: none;
  border: 0;
  border-radius: var(--dk-radius-control);
  cursor: pointer;
  color: inherit;
  line-height: 1;
}
.dk-drawer__x:hover {
  background: var(--dk-surface);
}

/* in-cart recommendations */

/* footer savings + trust badges */

/* slide + fade transition */
.dk-drawer-enter-active,
.dk-drawer-leave-active {
  transition: opacity 0.25s ease;
}
.dk-drawer-enter-active .dk-drawer__panel,
.dk-drawer-leave-active .dk-drawer__panel {
  transition: transform 0.28s cubic-bezier(0.16, 0.84, 0.44, 1);
}
.dk-drawer-enter-from,
.dk-drawer-leave-to {
  opacity: 0;
}
.dk-drawer-enter-from .dk-drawer__panel,
.dk-drawer-leave-to .dk-drawer__panel {
  transform: translateX(100%);
}
@media (prefers-reduced-motion: reduce) {
  .dk-drawer-enter-active,
  .dk-drawer-leave-active,
  .dk-drawer-enter-active .dk-drawer__panel,
  .dk-drawer-leave-active .dk-drawer__panel {
    transition: none;
  }
}
</style>
